/**
 * Display formatting for readouts. Pure helpers, unit-tested alongside the
 * range math.
 */

/** Round to `digits` significant-ish decimals, dropping trailing zeros. */
export function fmtNumber(v: number, digits = 1): string {
  if (!Number.isFinite(v)) return '—';
  const rounded = Number(v.toFixed(digits));
  return rounded.toLocaleString(undefined, { maximumFractionDigits: digits });
}

/** Seconds → "7h 24m", or "—" for null/zero. */
export function fmtDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export type TrendDir = 'up' | 'down' | 'flat';

export interface Delta {
  dir: TrendDir;
  /** Signed magnitude (latest − prior). */
  value: number;
}

/** Compare latest vs a prior baseline into a direction + magnitude. */
export function delta(latest: number, prior: number, epsilon = 1e-9): Delta {
  const d = latest - prior;
  if (Math.abs(d) <= epsilon) return { dir: 'flat', value: 0 };
  return { dir: d > 0 ? 'up' : 'down', value: d };
}

/** Sum the values of a metric point list (for "today" totals). */
export function sumPoints(points: { v: number }[]): number {
  return points.reduce((acc, p) => acc + (Number.isFinite(p.v) ? p.v : 0), 0);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2024-07-31" → "Jul 31, 2024". Returns the input unchanged if not YYYY-MM-DD. */
export function fmtYmd(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const [, y, mo, d] = m;
  const month = MONTHS[Number(mo) - 1];
  if (!month) return ymd;
  return `${month} ${Number(d)}, ${y}`;
}
